import type {
  AnalyticsAliasCallback,
  AnalyticsAnonIdCallback,
  AnalyticsIdentifyCallback,
  AnalyticsPageCallback,
  AnalyticsReadyCallback,
  AnalyticsResetCallback,
  AnalyticsTrackCallback
} from '@bo-types/analytics';
import {
  type IdentifyTraits,
  RudderAnalytics
} from '@rudderstack/analytics-js';
import {
  RUDDERSTACK_DATA_PLANE_URL,
  RUDDERSTACK_WRITE_KEY
} from '@bo-utils/environmentVariables';
import { useCallback, useMemo } from 'react';
import { RUDDERSTACK_LOAD_OPTIONS } from '@bo-constants/analytics/pageViews';

const rsWriteKey = RUDDERSTACK_WRITE_KEY || String(window.backoffice.rsKey);
const rsDataPlaneUrl =
  RUDDERSTACK_DATA_PLANE_URL || String(window.backoffice.rsUrl);

// Disable RudderStack for local, preview, mini env. and automated testing
const isAnalyticsDisabled =
  window.backoffice?.isLocalOrPreviewEnv ||
  window.backoffice?.isAutomatedTesting ||
  (!rsWriteKey && !rsDataPlaneUrl);

// Initialize and load RudderStack SDK
const initializeRudderStack = () => {
  if (isAnalyticsDisabled) {
    // eslint-disable-next-line no-console
    console.warn('Rudderstack analytics is disabled');
    return {} as RudderAnalytics;
  }

  const rudderAnalytics = new RudderAnalytics();
  rudderAnalytics.load(rsWriteKey, rsDataPlaneUrl, RUDDERSTACK_LOAD_OPTIONS);
  return rudderAnalytics;
};

const rudderAnalytics = initializeRudderStack();

// Clean Rudderstack storage after loading the SDK
const cleanRudderstackStorage = () => {
  const localStorageKeys = Object.keys({ ...localStorage });
  localStorageKeys.forEach((key: string) => {
    if (key.startsWith('rudder')) {
      localStorage.removeItem(key);
    }
  });
};

cleanRudderstackStorage();

/**
 * Hook to use RudderStack analytics.
 * @returns {AnalyticsMethods} Analytics: An object containing methods to track, page, identify, alias, reset, ready and getAnonymousId.
 * @see {@link https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/} RudderStack JS SDK documentation.
 * @description This hook is used to track events, page views, etc. using the RudderStack SDK.
 * @example
 * import { useAnalytics } from 'hooks/useAnalytics';
 * const { track } = useRudderStackAnalytics();
 * track('event_name', { property: 'value' });
 */
export const useAnalytics = () => {
  const track = useCallback<AnalyticsTrackCallback>(
    (name, props, options, callback) => {
      rudderAnalytics.track(name, props, options, callback);
    },
    []
  );

  const identify = useCallback<AnalyticsIdentifyCallback>(
    (userId, userData) => {
      rudderAnalytics.identify(userId, userData as IdentifyTraits);
    },
    []
  );

  const page = useCallback<AnalyticsPageCallback>((pageName, properties) => {
    rudderAnalytics.page(pageName, properties);
  }, []);

  const alias = useCallback<AnalyticsAliasCallback>((id) => {
    rudderAnalytics.alias(id);
  }, []);

  const reset = useCallback<AnalyticsResetCallback>(() => {
    rudderAnalytics.reset(true);
  }, []);

  const ready = useCallback<AnalyticsReadyCallback>((callback) => {
    rudderAnalytics.ready(() => {
      callback();
    });
  }, []);

  const getAnonymousId = useCallback<AnalyticsAnonIdCallback>(
    () => rudderAnalytics.getAnonymousId(),
    []
  );

  return useMemo(
    () => ({
      alias,
      getAnonymousId,
      identify,
      isAnalyticsDisabled,
      page,
      ready,
      reset,
      track
    }),
    [alias, getAnonymousId, identify, page, ready, reset, track]
  );
};
